import CampanaNotificaciones from "./CampanaNotificaciones";
import BotonInstructivo from "./BotonInstructivo";
import BotonMapaProceso from "./BotonMapaProceso";
import { useState, useEffect, useRef } from "react";
import { subirDatos, eliminarDato } from './syncSupabase';
import { integrarStockDesdeIngreso } from './StockMateriales';
import JsBarcode from "jsbarcode";
import { jsPDF } from "jspdf";
const BUCKET_CERTIFICADOS = "certificados-calidad";
const TIPOS_CERTIFICADO_PERMITIDOS = ["application/pdf", "image/jpeg", "image/jpg", "image/png"];
// Sube UN certificado a Storage con el tenant_id como primer segmento del
// path -- las policies RLS del bucket (ver setup_bucket_certificados_calidad.sql)
// filtran exactamente por ese segmento, así que el path define el aislamiento.
// El índice va en el nombre para que varios archivos del mismo ingreso (en
// el mismo guardado, mismo timestamp) no se pisen entre sí.
async function subirUnCertificado(archivo, tenantId, codigoIngreso, timestampBase, indice) {
  if (!tenantId) throw new Error("Sesión sin tenant_id -- no se puede subir el archivo.");
  const { supabase } = await import('./supabase');
  const ext = (archivo.name.split('.').pop() || 'pdf').toLowerCase();
  const path = `${tenantId}/${codigoIngreso}_${timestampBase}_${indice}.${ext}`;
  const { error } = await supabase.storage.from(BUCKET_CERTIFICADOS)
    .upload(path, archivo, { contentType: archivo.type, upsert: true });
  if (error) throw error;
  return { path, nombre: archivo.name, tipo: archivo.type };
}
// Sube varios certificados en paralelo, devuelve el array {path,nombre,tipo}
// listo para guardar en el campo `certificados` del ingreso.
async function subirCertificados(archivos, tenantId, codigoIngreso) {
  const timestampBase = Date.now();
  return Promise.all(archivos.map((archivo, i) => subirUnCertificado(archivo, tenantId, codigoIngreso, timestampBase, i + 1)));
}
// Formato viejo (Fase 12): un solo certificadoPath/certificadoNombre/
// certificadoTipo suelto en el ingreso. Formato nuevo: array `certificados`.
// Los dos conviven -- los ingresos viejos nunca se migran, solo se leen así.
function certificadosDe(item) {
  if (item?.certificados) return item.certificados;
  if (item?.certificadoPath) return [{ path: item.certificadoPath, nombre: item.certificadoNombre, tipo: item.certificadoTipo }];
  return [];
}
const STORAGE_IM = 'alqos_ingreso_materiales';
const STORAGE_NC = 'alqos_ncs';
const storage = {
  get: (k) => { try { return JSON.parse(localStorage.getItem(k)||'[]'); } catch { return []; } },
  set: (k,v) => { try { localStorage.setItem(k,JSON.stringify(v)); return true; } catch { return false; } },
};
const TIPOS_MATERIAL = [
  "Papel","Cartón macizo","Cartulina","Plancha offset","Tinta","Barniz",
  "Adhesivo","Film","Caja/embalaje","Insumo químico","Otro"
];
// Materiales que en el remito vienen en calibre, no en gramaje (Cartón
// macizo). Es el mismo dato interno (campo "gramaje"), pero acá donde se
// muestra hay que rotularlo como calibre -- es solo un dato de espesor, y
// además se usa para el arqueo FSC, así que el dato en sí no se toca en
// ningún lado, solo cómo se etiqueta.
const TIPOS_CALIBRE = ["Cartón macizo"];
const etiquetaGramaje = (tipoMaterial, valor) => {
  if (!valor) return "";
  return TIPOS_CALIBRE.includes(tipoMaterial) ? `${valor} cal.` : `${valor} g/m²`;
};
const UNIDADES = ["kg","g","pliegos","resmas","litros","unidades","metros"];
const PROVEEDORES_RAPIDOS = ["Proveedor habitual","Proveedor alternativo","Importado","Otro"];
const CONTENEDORES = [
  { id:"PAL", label:"Pallet",      icono:"📦" },
  { id:"BOB", label:"Bobina",      icono:"🧻" },
  { id:"TAC", label:"Tacho/Bidon", icono:"🪣" },
  { id:"CAJ", label:"Caja",        icono:"📫" },
  { id:"TAM", label:"Tambor",      icono:"🛢️" },
  { id:"RES", label:"Resma",       icono:"📋" },
  { id:"UNI", label:"Unidad",      icono:"🔧" },
];
const estadoColor = {
  aceptado:             "#22c55e",
  cuarentena:           "#f59e0b",
  devuelto:             "#e63946",
  aceptado_condicionado:"#3b82f6",
};
const estadoLabel = {
  aceptado:             "✅ Aceptado",
  cuarentena:           "⏳ En cuarentena",
  devuelto:             "🔴 Devuelto",
  aceptado_condicionado:"🔵 Aceptado condicionado",
};
// Helper genérico: busca el sufijo numérico más alto ya usado en una lista de
// códigos con el patrón PREFIJO-AAAA-NNN, y devuelve el próximo. Reemplaza el
// viejo patrón de "lista.length+1" en TODOS los lugares que generan códigos
// (ingresos, NCs, pallets) — length+1 repite códigos apenas la lista está
// incompleta (memoria local vieja, filtro, caché no sincronizada con
// Supabase), que fue justamente la causa del bug de IM-2026-001 duplicado.
function proximoNumero(codigos, prefijo) {
  const re = new RegExp(`^${prefijo}-\\d{4}-(\\d+)$`);
  let maxNum = 0;
  codigos.forEach(c => {
    const m = re.exec(c || "");
    if (m) maxNum = Math.max(maxNum, parseInt(m[1], 10));
  });
  return maxNum + 1;
}
function generarId(lista) {
  const anio = new Date().getFullYear();
  const n = proximoNumero(lista.map(i=>i?.codigoIngreso), "IM");
  return `IM-${anio}-${String(n).padStart(3,"0")}`;
}
function generarNCDesdeRechazo(item, usuario) {
  try {
    const ncs = storage.get(STORAGE_NC);
    const anio = new Date().getFullYear();
    const nci = `NCI-${anio}-${String(proximoNumero(ncs.map(n=>n?.nci), "NCI")).padStart(3,"0")}`;
    const nueva = {
      nci, id: Date.now()+1,
      origen: "Recepción MP",
      tipoNC: "PROVEEDOR",
      proceso: "Recepción MP",
      descripcion: `Material rechazado en recepción — ${item.codigoIngreso}: ${item.tipoMaterial} de ${item.proveedor}. Motivo: ${item.motivoRechazo||"Sin especificar"}`,
      severidad: "mayor",
      escalamiento: "medio",
      estado: "abierta",
      accionInmediata: "Material en cuarentena / devolución al proveedor",
      responsable: item.responsable||"",
      fechaHora: new Date().toISOString().slice(0,16),
      creadoPor: usuario?.nombre||"Sistema",
      rolCreador: usuario?.rol||"",
      generadaAutomaticamente: true,
      _generadaDesde: "ingreso_materiales",
      idIngreso: item.codigoIngreso,
      _source: "localStorage",
      _schema: "nc_v1",
    };
    storage.set(STORAGE_NC, [...ncs, nueva]);
    subirDatos(STORAGE_NC);
    return nci;
  } catch { return null; }
}
// ── Helpers de rótulos/código de barras, compartidos por la carga inline y por
// la pantalla de "Dividir en pallets" (post-hoc, para agregar pallets a un
// ingreso ya guardado).
// Nota importante (13/08): JsBarcode se usaba antes cargándolo en tiempo real
// desde un CDN (cdn.jsdelivr.net) con un <script> dinámico — eso fallaba en
// producción (probablemente el sitio bloquea scripts externos por seguridad/
// CSP), tanto para JsBarcode como para jsPDF. Se saca esa dependencia de CDN
// por completo: ahora "jsbarcode" es una dependencia normal de npm (import
// de arriba) que Vercel empaqueta junto con el resto del código de ALQOS, así
// que siempre está disponible al instante, sin red ni bloqueos posibles. ──
function generarBarcodeDataUrl(codigo) {
  const canvas = document.createElement("canvas");
  try { JsBarcode(canvas, codigo, { format:"CODE128", width:2, height:60, displayValue:true, fontSize:13 }); } catch {}
  return canvas.toDataURL("image/png");
}
// Arma el PDF con un rótulo por pallet (uno por página, 100×150mm — tamaño
// estándar de etiqueta térmica 4×6" para impresoras tipo Zebra). Márgenes
// chicos y todo en blanco/negro con acentos de color mínimos, porque la
// mayoría de las impresoras térmicas (Zebra incluida, si es de transferencia
// directa) imprimen solo en un color — el diseño tiene que leerse bien
// incluso si el color no sale. jsPDF es una dependencia empaquetada (import
// de arriba), no depende de ningún CDN ni de conexión.
// Importante al imprimir: en el diálogo de impresión elegí "Tamaño real /
// 100%" (NO "Ajustar a la página") y seleccioná el papel/etiqueta 10×15cm
// (4×6") en la impresora — si no, el PDF sale escalado y no calza con la
// etiqueta física.
// Logo opcional del tenant (cacheado en base64 al loguear, ver
// cachearLogoEmpresa en GestorUsuarios.js). Sin logo cargado, el rótulo
// sigue funcionando solo con el nombre -- no bloqueante.
function leerLogoEmpresa() {
  try {
    const dataUrl = localStorage.getItem("alqos_logo_empresa");
    const ratio = parseFloat(localStorage.getItem("alqos_logo_ratio"));
    if (dataUrl && ratio > 0) return { dataUrl, ratio, formato: dataUrl.startsWith("data:image/png") ? "PNG" : "JPEG" };
  } catch {}
  return null;
}
function construirPdfRotulos(lista, nombreEmpresa) {
  const doc = new jsPDF({ unit:"mm", format:[100,150] });
  const anchoPagina = doc.internal.pageSize.getWidth();
  const m = 6; // margen chico, para aprovechar toda la etiqueta
  // ALQOS es el nombre del software, no el de la empresa dueña del pallet --
  // el rótulo va a otras empresas (proveedores, transportistas), así que
  // tiene que mostrar el nombre real del tenant, no "ALQOS". Si no hay
  // nombre configurado (o es el valor demo), cae al nombre del software.
  const marca = (nombreEmpresa && nombreEmpresa !== "ALQOS™") ? nombreEmpresa : "ALQOS";
  const logo = leerLogoEmpresa();
  // Espacio de cabecera muy chico (etiqueta térmica 100mm) -- el logo va
  // chico a la izquierda del nombre, con presupuesto de ancho acotado para
  // no invadir el código de pallet que va a la derecha.
  const altoLogo = logo ? 7 : 0;
  const anchoLogo = logo ? Math.min(altoLogo * logo.ratio, 16) : 0;
  const xTexto = m + (logo ? anchoLogo + 2 : 0);
  lista.forEach((pallet, idx) => {
    if (idx > 0) doc.addPage();
    if (logo) { try { doc.addImage(logo.dataUrl, logo.formato, m, m-1, anchoLogo, altoLogo); } catch {} }
    doc.setFont(undefined,"bold"); doc.setTextColor(9,24,77);
    // El nombre de una pyme puede no entrar en el ancho disponible (mitad
    // de la etiqueta -- el código de pallet va a la derecha) al tamaño de
    // fuente original -- se achica hasta que entre, en vez de superponerse
    // con el código.
    let fs = 14;
    const anchoDisponible = (anchoPagina - 2*m) * 0.55 - (logo ? anchoLogo + 2 : 0);
    doc.setFontSize(fs);
    while (fs > 8 && doc.getTextWidth(marca) > anchoDisponible) { fs -= 1; doc.setFontSize(fs); }
    doc.text(marca, xTexto, m+6);
    doc.setFontSize(10);
    doc.text(String(pallet.codigoPallet||""), anchoPagina-m, m+6, { align:"right" });
    doc.setDrawColor(9,24,77); doc.setLineWidth(0.5);
    doc.line(m, m+9, anchoPagina-m, m+9);
    doc.addImage(generarBarcodeDataUrl(pallet.codigoPallet), "PNG", m, m+13, anchoPagina-2*m, 22);
    const campos = [
      ["Tipo", pallet.tipoMaterial], ["Proveedor", pallet.proveedor],
      ["Lote", pallet.lote||"—"], ["Fecha", pallet.fechaIngreso],
      ["Cantidad", `${pallet.cantidadInicial} ${pallet.unidad}`],
      [TIPOS_CALIBRE.includes(pallet.tipoMaterial) ? "Calibre" : "Gramaje", pallet.gramaje?etiquetaGramaje(pallet.tipoMaterial, pallet.gramaje):"—"],
      ["Medida", pallet.medida||"—"],
      ["Ingreso", `${pallet.codigoIngreso}${pallet.remito?" Rem."+pallet.remito:""}`],
    ];
    const yBase = m + 44;
    campos.forEach(([k,v], i) => {
      const col = i % 2, fila = Math.floor(i/2);
      const x = m + col*((anchoPagina-2*m)/2);
      const y = yBase + fila*13;
      doc.setFont(undefined,"normal"); doc.setFontSize(7); doc.setTextColor(120,120,120);
      doc.text(k.toUpperCase(), x, y);
      doc.setFont(undefined,"bold"); doc.setFontSize(9.5); doc.setTextColor(9,24,77);
      doc.text(String(v||"—"), x, y+5);
    });
    if (pallet.esFSC) {
      const yFsc = yBase + Math.ceil(campos.length/2)*13 + 3;
      doc.setDrawColor(21,128,61); doc.setLineWidth(0.5);
      doc.rect(m, yFsc, anchoPagina-2*m, 8);
      doc.setFont(undefined,"bold"); doc.setFontSize(8.5); doc.setTextColor(21,128,61);
      doc.text("MATERIAL FSC CERTIFICADO", anchoPagina/2, yFsc+5.5, { align:"center" });
    }
  });
  return doc;
}
// Genera el PDF y lo descarga directo — sin abrir ninguna pestaña ni pasar
// por el diálogo de impresión del navegador. Al ser todo local (sin CDN),
// esto corre sincrónico y no puede quedar colgado por un bloqueo de red.
function imprimirPalletsLote(lista, nombreEmpresa) {
  if (!lista?.length) return;
  try {
    const doc = construirPdfRotulos(lista, nombreEmpresa);
    const nombreArchivo = `Rotulos_${lista[0]?.codigoIngreso||"pallets"}_${lista.length}u.pdf`;
    doc.save(nombreArchivo);
  } catch (err) {
    console.error("Error generando PDF de rótulos:", err);
    alert("No se pudo generar el PDF de rótulos.\nMotivo: " + (err?.message || "desconocido") + "\n\nProbá de nuevo; si persiste, avisame este mensaje.");
  }
}
export default function IngresoMateriales({ onVolver, oscuro, usuario }) {
  const [items, setItems]     = useState([]);
  const [cargando, setCargando] = useState(true);
  const [vista, setVista]     = useState("listado");
  const [confirmEliminar, setConfirmEliminar] = useState(false);
  const [ingresoParaPallets, setIngresoParaPallets] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [busqueda, setBusqueda] = useState("");
  const [filtroEstado, setFiltroEstado] = useState("todos");
  const [filtroTipo] = useState("todos");
  const [ncGenerada, setNcGenerada] = useState(null);
  // Id del ingreso que se está corrigiendo (no null = estamos en modo edición,
  // reutilizamos la misma pantalla "nuevo" pero actualizando el registro
  // existente en vez de crear uno nuevo con pallets/rótulos).
  const [editandoId, setEditandoId] = useState(null);
  const [form, setForm] = useState({
    tipoMaterial:"Papel", unidad:"kg", estado:"aceptado",
    fecha: new Date().toISOString().slice(0,10),
    esFSC: false, tieneRemito: false,
  });
  // Certificados de calidad: pueden ser varios por ingreso. archivosNuevos
  // son File elegidos en esta sesión de formulario (transitorios, no van al
  // form porque un File no es serializable a localStorage). certificadosExistentes
  // son los {path,nombre,tipo} que el ingreso ya tenía guardados (solo se
  // pueblan en modo edición) -- ambas listas se muestran juntas, cada una
  // removible antes de guardar.
  const [archivosNuevos, setArchivosNuevos] = useState([]);
  const [certificadosExistentes, setCertificadosExistentes] = useState([]);
  const [guardando, setGuardando] = useState(false);
  // Dos inputs ocultos en vez de uno solo con accept mixto -- capture solo
  // dispara la cámara de forma confiable (Chrome Android en particular)
  // cuando accept es exclusivamente image/*; con PDF+imagen mezclado en el
  // mismo input, capture se ignora en varios navegadores móviles.
  const inputFotoCertificadoRef = useRef(null);
  const inputArchivoCertificadoRef = useRef(null);
  // URLs firmadas (5 min) de los certificados del ingreso abierto en
  // detalle -- el bucket es privado, así que no hay URL pública fija.
  // Mapa path -> url, uno por cada archivo adjunto (puede haber varios).
  const [certificadosUrls, setCertificadosUrls] = useState({});
  // ── Pallets a generar junto con este ingreso (carga inline, sin pasar por
  // una segunda pantalla) ──
  const [palletsDraft, setPalletsDraft] = useState([]);
  const [formPallet, setFormPallet] = useState({ contenedor:"PAL", cantidad:"", lote:"" });
  const bg     = oscuro ? "#09184D" : "#F0EEFF";
  const cardBg = oscuro ? "#0d2160" : "white";
  const texto  = oscuro ? "#EDF2FA" : "#2F3E2E";
  const borde  = oscuro ? "#1e3a8a" : "#eee";
  const navBg  = oscuro ? "#060f30" : "#09184D";
  const sub    = oscuro ? "#9aabd4" : "#808085";
  const card   = { background:cardBg, borderRadius:"12px", padding:"1.5rem", border:`1px solid ${borde}`, marginBottom:"12px" };
  const inp    = { width:"100%", padding:"8px 12px", borderRadius:"8px", border:`1px solid ${borde}`, fontSize:"13px", boxSizing:"border-box", marginTop:"4px", background:cardBg, color:texto, fontFamily:"sans-serif" };
  const lbl    = { fontSize:"11px", color:sub, fontWeight:"700", display:"block", marginTop:"12px", textTransform:"uppercase", letterSpacing:".05em" };
  const esCalidad = ["Administrador","Calidad"].includes(usuario?.rol);
  useEffect(() => {
    async function cargar() {
      try {
        const { supabase } = await import('./supabase');
        const [{ data }, { data: dataPallets }] = await Promise.all([
          supabase.from('ingreso_materiales').select('data'),
          supabase.from('stock_pallets').select('data'),
        ]);
        if (data && data.length > 0) {
          const remotos = data.map(r=>r.data);
          setItems(remotos);
          // Clave: si no persistimos acá lo que vino de Supabase, la memoria
          // local del navegador queda vieja/vacía aunque en pantalla se vea
          // todo bien — y guardar() (que lee de esa memoria local para generar
          // el próximo código IM-...) puede terminar repitiendo un código ya
          // usado. Este fix mantiene ambas copias siempre sincronizadas.
          storage.set(STORAGE_IM, remotos);
        }
        else setItems(storage.get(STORAGE_IM));
        // Mismo motivo que arriba, pero para los pallets: generarPalletCode
        // (en guardar() y en la vista de pallets) calcula el próximo código
        // a partir de storage.get("alqos_stock_pallets"). Si esta PC nunca
        // abrió el módulo de Stock, esa caché local puede estar vacía o
        // vieja y el código generado puede chocar con uno real que ya existe
        // en Supabase generado desde otra PC. La sincronizamos acá también.
        if (dataPallets && dataPallets.length > 0) {
          storage.set("alqos_stock_pallets", dataPallets.map(r=>r.data));
        }
      } catch { setItems(storage.get(STORAGE_IM)); }
      setCargando(false);
    }
    cargar();
  }, []);
  useEffect(() => {
    let cancelado = false;
    setCertificadosUrls({});
    const lista = certificadosDe(detalle);
    if (!lista.length) return;
    (async () => {
      const { supabase } = await import('./supabase');
      const entradas = await Promise.all(lista.map(async (c) => {
        const { data, error } = await supabase.storage.from(BUCKET_CERTIFICADOS).createSignedUrl(c.path, 300);
        return error ? null : [c.path, data.signedUrl];
      }));
      if (!cancelado) setCertificadosUrls(Object.fromEntries(entradas.filter(Boolean)));
    })();
    return () => { cancelado = true; };
  }, [detalle]);
  // La "Cantidad total" del ingreso se autocalcula sumando los pallets que se
  // van cargando abajo — el remito no siempre trae un total general armado,
  // se arma pallet por pallet a medida que se lee. Si no hay ningún pallet
  // cargado (ingresos sin desglose, o estados cuarentena/devuelto), el campo
  // queda editable a mano como antes.
  const huboPalletsDraftRef = useRef(false);
  useEffect(() => {
    if (palletsDraft.length > 0) {
      huboPalletsDraftRef.current = true;
      const total = palletsDraft.reduce((acc,p)=>acc+(parseFloat(p.cantidad)||0),0);
      setForm(f => ({ ...f, cantidad: total || "" }));
    } else if (huboPalletsDraftRef.current) {
      // Solo limpiamos si ACÁ MISMO hubo pallets cargados y se borraron todos
      // (ej: cargás 2 pallets de 300kg, los borrás los dos con la ×) — sin
      // este chequeo, el valor autocalculado quedaba "pegado" en el campo y
      // se podía guardar por error un total fantasma sin ningún pallet real
      // detrás. El chequeo evita pisar form.cantidad al entrar en modo
      // edición (iniciarEdicion precarga un valor real y acá palletsDraft
      // arranca vacío, pero nunca hubo pallets cargados EN ESTA pantalla).
      huboPalletsDraftRef.current = false;
      setForm(f => (f.cantidad ? { ...f, cantidad: "" } : f));
    }
  }, [palletsDraft]);
  if (cargando) return (
    <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:bg }}>
      <p style={{ color:oscuro?"white":"#09184D", fontSize:"16px" }}>Cargando ingresos...</p>
    </div>
  );
  // ── Pallets: agregar/quitar de la lista temporal (antes de guardar el ingreso) ──
  const agregarPalletDraft = () => {
    // !formPallet.cantidad no alcanza: el string "0" es "truthy" en JS, así
    // que se podía cargar un pallet con cantidad 0 (o negativa) sin ninguna
    // alerta. Se valida el valor numérico real.
    if (!formPallet.cantidad || parseFloat(formPallet.cantidad) <= 0) { alert("Ingresá una cantidad mayor a cero."); return; }
    setPalletsDraft(pl => [...pl, { ...formPallet, _key: Date.now()+Math.random() }]);
    setFormPallet({ contenedor: formPallet.contenedor, cantidad:"", lote: formPallet.lote });
  };
  const quitarPalletDraft = (key) => {
    setPalletsDraft(pl => pl.filter(p => p._key !== key));
  };
  // ── Guardar ingreso + (si se cargaron) todos sus pallets, en un solo paso.
  // Si no se cargó ningún pallet a mano, se mantiene el comportamiento viejo:
  // se genera automáticamente 1 pallet vía integrarStockDesdeIngreso(). ──
  // Corrige los datos de un ingreso ya guardado (material, proveedor, remito,
  // inspección, etc.) SIN tocar los pallets ya generados ni el código de
  // ingreso — solo actualiza el registro existente.
  const guardarEdicion = async () => {
    if (!form.tipoMaterial||!form.proveedor?.trim()) {
      alert("Completá tipo de material y proveedor."); return;
    }
    const original = items.find(i => i.id === editandoId);
    setGuardando(true);
    // certificadosExistentes ya refleja lo que quedó después de que el
    // usuario haya quitado alguno con el ✕ de la lista del formulario --
    // acá solo hace falta subir los nuevos y combinar.
    let certificadosFinal = certificadosExistentes;
    if (archivosNuevos.length) {
      try {
        const subidos = await subirCertificados(archivosNuevos, usuario?.tenant_id, original?.codigoIngreso || editandoId);
        certificadosFinal = [...certificadosExistentes, ...subidos];
      } catch (e) {
        alert("No se pudo subir uno o más certificados nuevos: " + e.message + "\n\nLos demás cambios no se guardaron -- probá de nuevo.");
        setGuardando(false);
        return;
      }
    }
    const actualizados = items.map(i => i.id === editandoId ? {
      ...i,
      tipoMaterial: form.tipoMaterial,
      gramaje: form.gramaje,
      medidaPliego: form.medidaPliego,
      // Si ya tiene pallets reales generados, la cantidad NO se toca desde acá
      // (queda como refuerzo aunque el campo ya esté bloqueado en la UI) —
      // para corregirla hay que corregir los pallets en Stock de Materiales,
      // así el total del ingreso nunca queda desincronizado de lo que
      // físicamente existe. Si no tiene pallets reales: antes era
      // "parseFloat(form.cantidad)||i.cantidad", que trataba un 0 tipeado
      // a propósito como "sin valor" y conservaba el valor viejo en
      // silencio — se chequea contra NaN en vez de falsy.
      cantidad: i.palletGenerado ? i.cantidad : (()=>{ const n = parseFloat(form.cantidad); return Number.isNaN(n) ? i.cantidad : n; })(),
      unidad: form.unidad,
      lote: form.lote,
      proveedor: form.proveedor,
      remito: form.remito,
      fecha: form.fecha,
      tieneRemito: form.tieneRemito,
      certificados: certificadosFinal,
      // Campos viejos sueltos: se limpian en los registros que se editan de
      // ahora en más (certificadosDe() ya no los necesita si hay `certificados`,
      // y dejarlos pisaría datos si alguna vez se reemplazan con otro archivo
      // en el índice 1). Los ingresos que NO se editan quedan con el campo
      // viejo intacto, sin tocar.
      certificadoPath: undefined, certificadoNombre: undefined, certificadoTipo: undefined,
      tieneCertificado: certificadosFinal.length > 0,
      esFSC: form.esFSC,
      inspeccionVisual: form.inspeccionVisual,
      observaciones: form.observaciones,
      motivoRechazo: form.motivoRechazo,
      editadoPor: usuario?.nombre||"Sistema",
      fechaEdicion: new Date().toISOString(),
    } : i);
    setItems(actualizados);
    storage.set(STORAGE_IM, actualizados);
    subirDatos(STORAGE_IM);
    const actualizado = actualizados.find(i=>i.id===editandoId);
    setEditandoId(null);
    setArchivosNuevos([]);
    setCertificadosExistentes([]);
    setGuardando(false);
    setDetalle(actualizado);
    setVista("detalle");
  };
  const iniciarEdicion = (item) => {
    setForm({
      tipoMaterial: item.tipoMaterial, gramaje: item.gramaje, medidaPliego: item.medidaPliego,
      cantidad: item.cantidad, unidad: item.unidad, lote: item.lote,
      proveedor: item.proveedor, remito: item.remito, fecha: item.fecha,
      tieneRemito: item.tieneRemito, esFSC: item.esFSC,
      estado: item.estado, inspeccionVisual: item.inspeccionVisual,
      observaciones: item.observaciones, motivoRechazo: item.motivoRechazo,
    });
    setArchivosNuevos([]);
    setCertificadosExistentes(certificadosDe(item));
    setEditandoId(item.id);
    setPalletsDraft([]);
    setVista("nuevo");
  };
  const guardar = async () => {
    if (!form.tipoMaterial||!form.proveedor?.trim()) {
      alert("Completá tipo de material y proveedor."); return;
    }
    const cantidadTotal = palletsDraft.length > 0
      ? palletsDraft.reduce((acc,p)=>acc+(parseFloat(p.cantidad)||0),0)
      : parseFloat(form.cantidad)||0;
    if (!cantidadTotal) {
      alert("Cargá la cantidad total, o agregá al menos un pallet abajo con su cantidad."); return;
    }
    setGuardando(true);
    const actuales = storage.get(STORAGE_IM);
    const codigoIngreso = generarId(actuales);
    let certificados = [];
    if (archivosNuevos.length) {
      try {
        certificados = await subirCertificados(archivosNuevos, usuario?.tenant_id, codigoIngreso);
      } catch (e) {
        alert("No se pudo subir uno o más certificados: " + e.message + "\n\nEl ingreso no se guardó -- probá de nuevo.");
        setGuardando(false);
        return;
      }
    }
    let ncGen = null;
    if (form.estado === "devuelto") {
      ncGen = generarNCDesdeRechazo({...form, codigoIngreso}, usuario);
    }
    const nuevo = {
      ...form,
      cantidad: cantidadTotal,
      id: Date.now(),
      codigoIngreso,
      certificados,
      tieneCertificado: certificados.length > 0,
      fechaCreacion: new Date().toISOString(),
      creadoPor: usuario?.nombre||"Sistema",
      rolCreador: usuario?.rol||"",
      ncGenerada: ncGen,
      historialEstados: [{ estado: form.estado, por: usuario?.nombre, fecha: new Date().toISOString() }],
    };
    let codigosPallets = [];
    let palletsRecienCreados = [];
    if (["aceptado", "aceptado_condicionado"].includes(nuevo.estado)) {
      if (palletsDraft.length > 0) {
        const stockActual = storage.get("alqos_stock_pallets");
        // Mismo problema que tenía generarId() con los códigos de ingreso: acá
        // se calculaba con stockActual.length+idx+1, y stockActual sale de una
        // caché local de "alqos_stock_pallets" que esta pantalla nunca
        // sincroniza con Supabase al abrirse — si esta PC nunca abrió el
        // módulo de Stock, esa caché puede estar vacía o vieja, generando
        // códigos de pallet ya usados en otro lugar. Se calcula por contador
        // de máximo existente (por prefijo de contenedor: PAL, CAJA, etc.),
        // igual que ya se hace para IM-... y NCI-....
        const codigosPalletExistentes = stockActual.map(s=>s.codigoPallet);
        const anioPallet = new Date().getFullYear();
        const contadoresPallet = {};
        palletsRecienCreados = palletsDraft.map((p, idx) => {
          const prefijo = p.contenedor;
          contadoresPallet[prefijo] = (prefijo in contadoresPallet)
            ? contadoresPallet[prefijo] + 1
            : proximoNumero(codigosPalletExistentes, prefijo);
          const codigoPallet = `${prefijo}-${anioPallet}-${String(contadoresPallet[prefijo]).padStart(4,"0")}`;
          return {
          id: Date.now() + idx,
          codigoPallet,
          tipoMaterial: nuevo.tipoMaterial,
          proveedor: nuevo.proveedor,
          remito: nuevo.remito || "",
          lote: p.lote || nuevo.lote || "",
          gramaje: nuevo.gramaje || "",
          medida: nuevo.medidaPliego || "",
          unidad: nuevo.unidad,
          cantidadInicial: parseFloat(p.cantidad)||0,
          cantidadActual: parseFloat(p.cantidad)||0,
          esFSC: nuevo.esFSC || false,
          estado: "disponible",
          codigoIngreso,
          fechaIngreso: nuevo.fecha,
          creadoPor: usuario?.nombre || "Sistema",
          fechaCreacion: new Date().toISOString(),
          contenedor: p.contenedor,
          historial: [],
          };
        });
        storage.set("alqos_stock_pallets", [...stockActual, ...palletsRecienCreados]);
        subirDatos("alqos_stock_pallets");
        codigosPallets = palletsRecienCreados.map(p=>p.codigoPallet);
      } else {
        const palletGenerado = integrarStockDesdeIngreso(nuevo, usuario);
        if (palletGenerado) codigosPallets = [palletGenerado];
      }
      if (codigosPallets.length) nuevo.palletGenerado = codigosPallets;
    }
    const nuevos = [...actuales, nuevo];
    storage.set(STORAGE_IM, nuevos);
    subirDatos(STORAGE_IM);
    setItems(nuevos);
    if (ncGen) setNcGenerada(ncGen);
    setForm({ tipoMaterial:"Papel", unidad:"kg", estado:"aceptado", fecha:new Date().toISOString().slice(0,10), esFSC:false, tieneRemito:false });
    setPalletsDraft([]);
    setFormPallet({ contenedor:"PAL", cantidad:"", lote:"" });
    setArchivosNuevos([]);
    setCertificadosExistentes([]);
    setGuardando(false);
    // Si se cargaron pallets a mano, imprimimos los rótulos de todos ya mismo —
    // sin pasar por una segunda pantalla — y mostramos el detalle del ingreso
    // recién creado para que quede a la vista qué se generó.
    if (palletsRecienCreados.length > 0) {
      imprimirPalletsLote(palletsRecienCreados, usuario?.empresa);
    }
    setDetalle(nuevo);
    setVista("detalle");
  };
  const cambiarEstado = (id, nuevoEstado) => {
    const item = items.find(i=>i.id===id);
    let ncGen = item.ncGenerada;
    if (nuevoEstado==="devuelto" && !item.ncGenerada) {
      ncGen = generarNCDesdeRechazo(item, usuario);
      if (ncGen) setNcGenerada(ncGen);
    }
    const entrada = { estado:nuevoEstado, por:usuario?.nombre, fecha:new Date().toISOString() };
    let actualizados = items.map(i=>i.id===id?{
      ...i, estado:nuevoEstado, ncGenerada:ncGen,
      historialEstados:[...(i.historialEstados||[]),entrada]
    }:i);
    // Ojo: antes acá se llamaba setItems(actualizados) YA, y recién después se
    // generaba el pallet — con lo cual el pallet quedaba bien guardado en
    // Supabase/localStorage pero la pantalla (items en memoria y el detalle
    // abierto) nunca se enteraba de palletGenerado. Resultado: al aceptar un
    // material que estaba en cuarentena, el botón "Imprimir rótulos" seguía
    // oculto como si no se hubiera generado nada, aunque sí se generó — había
    // que refrescar la página entera para verlo. Se corrige generando el
    // pallet PRIMERO y recién ahí actualizando el estado de React una sola vez
    // con el resultado ya completo.
    let palletCode = null;
    if (nuevoEstado === "aceptado" && !item.palletGenerado) {
      palletCode = integrarStockDesdeIngreso({ ...item, estado: "aceptado" }, usuario);
      if (palletCode) {
        actualizados = actualizados.map(i => i.id === id ? { ...i, palletGenerado: palletCode } : i);
      }
    }
    setItems(actualizados);
    storage.set(STORAGE_IM, actualizados);
    subirDatos(STORAGE_IM);
    if (detalle?.id===id) setDetalle(prev=>({
      ...prev, estado:nuevoEstado, ncGenerada:ncGen,
      historialEstados:[...(prev.historialEstados||[]),entrada],
      ...(palletCode ? { palletGenerado: palletCode } : {}),
    }));
  };
  const itemsFiltrados = items.filter(i=>{
    const matchEstado = filtroEstado==="todos"||i.estado===filtroEstado;
    const matchTipo   = filtroTipo==="todos"||i.tipoMaterial===filtroTipo;
    const matchBusq   = !busqueda||i.codigoIngreso?.toLowerCase().includes(busqueda.toLowerCase())||i.proveedor?.toLowerCase().includes(busqueda.toLowerCase())||i.tipoMaterial?.toLowerCase().includes(busqueda.toLowerCase());
    return matchEstado&&matchTipo&&matchBusq;
  });
  const resumen = {
    total:      items.length,
    aceptados:  items.filter(i=>i.estado==="aceptado"||i.estado==="aceptado_condicionado").length,
    cuarentena: items.filter(i=>i.estado==="cuarentena").length,
    devueltos:  items.filter(i=>i.estado==="devuelto").length,
    esFSC:      items.filter(i=>i.esFSC).length,
  };
  // ── VISTA PALLETS (post-hoc: agregar más pallets a un ingreso ya guardado) ──
  if (vista==="pallets" && ingresoParaPallets) {
    const PalletsView = () => {
      const [palletsLocales, setPalletsLocales] = useState(() => {
        const existentes = storage.get("alqos_stock_pallets");
        return existentes.filter(p => p.codigoIngreso === ingresoParaPallets.codigoIngreso);
      });
      const [formP, setFormP] = useState({
        contenedor:"PAL", cantidad:"", unidad: ingresoParaPallets.unidad || "kg",
        gramaje: ingresoParaPallets.gramaje || "", medida: ingresoParaPallets.medidaPliego || "",
        lote: ingresoParaPallets.lote || "", esFSC: ingresoParaPallets.esFSC || false,
      });
      const agregarPallet = () => {
        if (!formP.cantidad || parseFloat(formP.cantidad) <= 0) { alert("Ingresá una cantidad mayor a cero."); return; }
        const actuales = storage.get("alqos_stock_pallets");
        // Mismo fix que en guardar(): número por máximo existente, no por
        // longitud de la lista (que puede estar vieja/incompleta si esta PC
        // no sincronizó el stock recientemente).
        const cod = formP.contenedor + "-" + new Date().getFullYear() + "-" + String(proximoNumero(actuales.map(p=>p.codigoPallet), formP.contenedor)).padStart(4,"0");
        const nuevo = {
          id: Date.now(),
          codigoPallet: cod,
          tipoMaterial: ingresoParaPallets.tipoMaterial,
          proveedor: ingresoParaPallets.proveedor,
          remito: ingresoParaPallets.remito || "",
          lote: formP.lote || "",
          gramaje: formP.gramaje || "",
          medida: formP.medida || "",
          unidad: formP.unidad,
          cantidadInicial: parseFloat(formP.cantidad),
          cantidadActual: parseFloat(formP.cantidad),
          esFSC: formP.esFSC,
          estado: "disponible",
          codigoIngreso: ingresoParaPallets.codigoIngreso,
          fechaIngreso: ingresoParaPallets.fecha,
          creadoPor: usuario?.nombre || "Sistema",
          fechaCreacion: new Date().toISOString(),
          contenedor: formP.contenedor,
          historial: [],
        };
        const nuevos = [...actuales, nuevo];
        storage.set("alqos_stock_pallets", nuevos);
        subirDatos("alqos_stock_pallets");
        setPalletsLocales(pl => [...pl, nuevo]);
        // Actualizar palletGenerado en el ingreso
        const ims = storage.get(STORAGE_IM);
        const codigos = [...(Array.isArray(ingresoParaPallets.palletGenerado) ? ingresoParaPallets.palletGenerado : ingresoParaPallets.palletGenerado ? [ingresoParaPallets.palletGenerado] : []), cod];
        const actualizados = ims.map(i => i.id === ingresoParaPallets.id ? { ...i, palletGenerado: codigos } : i);
        storage.set(STORAGE_IM, actualizados);
        subirDatos(STORAGE_IM);
        setFormP(f => ({ ...f, cantidad:"", medida:"" }));
      };
      const imprimirTodos = (lista) => imprimirPalletsLote(lista, usuario?.empresa);
      const imprimirRotulo = (pallet) => imprimirPalletsLote([pallet], usuario?.empresa);
      const inp2 = { width:"100%", padding:"8px 12px", borderRadius:"8px", border:"1px solid "+borde, fontSize:"13px", boxSizing:"border-box", marginTop:"4px", background:cardBg, color:texto };
      const lbl2 = { fontSize:"11px", color:sub, fontWeight:"700", display:"block", marginTop:"12px", textTransform:"uppercase" };
      const esCalibreP = TIPOS_CALIBRE.includes(ingresoParaPallets.tipoMaterial);
      return (
        <div style={{ minHeight:"100vh", background:bg, fontFamily:"sans-serif" }}>
          <div style={{ background:navBg, padding:"1rem 1.5rem", display:"flex", alignItems:"center", gap:"12px", position:"sticky", top:0, zIndex:100 }}>
            <button onClick={() => setVista("detalle")} style={{ background:"rgba(255,255,255,.1)", border:"none", color:"white", padding:"6px 14px", borderRadius:"10px", cursor:"pointer", fontSize:"13px" }}>← Volver</button>
            <span style={{ color:"white", fontWeight:"700", fontSize:"16px" }}>📦 Pallets — {ingresoParaPallets.codigoIngreso}</span>
          </div>
          <div style={{ padding:"1.5rem", maxWidth:"700px", margin:"0 auto" }}>
            <div style={{ background:cardBg, borderRadius:"12px", padding:"1.2rem 1.5rem", border:"1px solid #7B5BF240", marginBottom:"12px" }}>
              <p style={{ fontSize:"12px", color:sub, margin:0 }}>Ingreso total: <strong style={{color:texto}}>{ingresoParaPallets.cantidad} {ingresoParaPallets.unidad}</strong> de <strong style={{color:texto}}>{ingresoParaPallets.tipoMaterial}</strong> · {ingresoParaPallets.proveedor}</p>
            </div>
            <div style={{ background:cardBg, borderRadius:"12px", padding:"1.2rem 1.5rem", border:"1px solid "+borde, marginBottom:"12px" }}>
              <h3 style={{ fontSize:"13px", fontWeight:"700", color:texto, marginBottom:"12px" }}>Agregar pallet / unidad</h3>
              <span style={lbl2}>Tipo de contenedor</span>
              <div style={{ display:"flex", gap:"6px", flexWrap:"wrap", marginTop:"6px" }}>
                {CONTENEDORES.map(c => (
                  <button key={c.id} type="button" onClick={() => setFormP(f => ({ ...f, contenedor: c.id }))}
                    style={{ padding:"6px 10px", borderRadius:"8px", fontSize:"11px", cursor:"pointer", fontWeight:"600", border: formP.contenedor === c.id ? "2px solid #7B5BF2" : "1px solid "+borde, background: formP.contenedor === c.id ? "#7B5BF215" : cardBg, color: formP.contenedor === c.id ? "#7B5BF2" : sub }}>
                    {c.icono} {c.label}
                  </button>
                ))}
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr", gap:"10px" }}>
                <div><span style={lbl2}>Cantidad *</span><input type="number" value={formP.cantidad} onChange={e => setFormP(f => ({ ...f, cantidad:e.target.value }))} style={inp2} placeholder="0"/></div>
                <div><span style={lbl2}>Unidad</span>
                  <select value={formP.unidad} onChange={e => setFormP(f => ({ ...f, unidad:e.target.value }))} style={inp2}>
                    {["kg","pliegos","resmas","litros","unidades","metros","bobinas","tachos"].map(u => <option key={u}>{u}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"10px" }}>
                <div><span style={lbl2}>{esCalibreP ? "Calibre" : "Gramaje g/m²"}</span><input type="number" step={esCalibreP?"0.1":"1"} value={formP.gramaje} onChange={e => setFormP(f => ({ ...f, gramaje:e.target.value }))} style={inp2} placeholder={esCalibreP?"1.5":"300"}/></div>
                <div><span style={lbl2}>Medida</span><input value={formP.medida} onChange={e => setFormP(f => ({ ...f, medida:e.target.value }))} style={inp2} placeholder="70x100"/></div>
              </div>
              <span style={lbl2}>Lote / Partida</span>
              <input value={formP.lote} onChange={e => setFormP(f => ({ ...f, lote:e.target.value }))} style={inp2} placeholder="N° lote"/>
              <label style={{ display:"flex", alignItems:"center", gap:"8px", marginTop:"12px", cursor:"pointer", fontSize:"12px", color:texto }}>
                <input type="checkbox" checked={formP.esFSC} onChange={e => setFormP(f => ({ ...f, esFSC:e.target.checked }))} style={{ width:"16px", height:"16px" }}/>
                Material FSC certificado
              </label>
              <button onClick={agregarPallet}
                style={{ width:"100%", marginTop:"16px", padding:"12px", background:"linear-gradient(135deg,#7B5BF2,#09184D)", color:"white", border:"none", borderRadius:"10px", fontSize:"14px", fontWeight:"700", cursor:"pointer" }}>
                + Agregar y generar código
              </button>
            </div>
            {palletsLocales.length > 0 && (
              <div style={{ background:cardBg, borderRadius:"12px", padding:"1.2rem 1.5rem", border:"1px solid "+borde }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"12px" }}>
                  <h3 style={{ fontSize:"13px", fontWeight:"700", color:texto, margin:0 }}>Pallets generados ({palletsLocales.length})</h3>
                  <button onClick={() => imprimirTodos(palletsLocales)}
                    style={{ padding:"8px 16px", background:"#09184D", color:"white", border:"none", borderRadius:"8px", cursor:"pointer", fontSize:"12px", fontWeight:"700" }}>
                    🖨️ Imprimir todos ({palletsLocales.length})
                  </button>
                </div>
                {palletsLocales.map(p => (
                  <div key={p.id} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 0", borderBottom:"0.5px solid "+borde }}>
                    <div>
                      <span style={{ fontWeight:"700", color:"#7B5BF2", fontSize:"13px" }}>{p.codigoPallet}</span>
                      <p style={{ fontSize:"11px", color:sub, margin:"2px 0 0" }}>{p.cantidadInicial} {p.unidad}{p.gramaje?" · "+etiquetaGramaje(p.tipoMaterial, p.gramaje):""}{p.medida?" · "+p.medida:""}</p>
                    </div>
                    <button onClick={() => imprimirRotulo(p)}
                      style={{ padding:"6px 14px", background:"#7B5BF215", border:"1px solid #7B5BF240", color:"#7B5BF2", borderRadius:"8px", cursor:"pointer", fontSize:"12px", fontWeight:"700" }}>
                      🖨️ Rótulo
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      );
    };
    return <PalletsView/>;
  }
  // ── DETALLE ──
  const eliminarIngreso = async (id) => {
    const nuevos = items.filter(i => i.id !== id);
    setItems(nuevos);
    storage.set(STORAGE_IM, nuevos);
    await eliminarDato(STORAGE_IM, id);
    setConfirmEliminar(false);
    setDetalle(null);
    setVista("listado");
  };
  if (vista==="detalle"&&detalle) return (
    <div style={{ minHeight:"100vh", background:bg, fontFamily:"sans-serif" }}>
      <div style={{ background:navBg, padding:"1rem 1.5rem", display:"flex", alignItems:"center", justifyContent:"space-between", position:"sticky", top:0, zIndex:100, marginBottom:"1.5rem" }}>
        <div style={{ display:"flex", alignItems:"center", gap:"12px" }}>
          <button onClick={()=>{setDetalle(null);setVista("listado");setNcGenerada(null);}} style={{ background:"rgba(255,255,255,0.1)", border:"none", color:"white", padding:"6px 14px", borderRadius:"10px", cursor:"pointer", fontSize:"13px" }}>← Volver</button>
          <span style={{ color:"white", fontWeight:"700", fontSize:"16px" }}>{detalle.codigoIngreso}</span>
        </div>
        <div style={{ display:"flex", gap:"8px" }}>
          {confirmEliminar ? (
            <>
              <span style={{ color:"white", fontSize:"12px", alignSelf:"center", maxWidth:"260px", textAlign:"right" }}>
                {/* Aviso concreto de lo que queda huérfano — antes decía solo
                    "¿Confirmar?" sin avisar que el ingreso puede tener pallets
                    en stock y/o una NC generada que van a quedar sin el
                    ingreso que los originó. */}
                {(() => {
                  const nPallets = Array.isArray(detalle.palletGenerado) ? detalle.palletGenerado.length : (detalle.palletGenerado ? 1 : 0);
                  const avisos = [];
                  if (nPallets > 0) avisos.push(`${nPallets} pallet${nPallets>1?"s":""} en stock`);
                  if (detalle.ncGenerada) avisos.push("una NC generada");
                  return avisos.length
                    ? `Este ingreso tiene ${avisos.join(" y ")} — van a quedar sin el ingreso que los originó. ¿Confirmar?`
                    : "¿Confirmar eliminación?";
                })()}
              </span>
              <button onClick={()=>eliminarIngreso(detalle.id)} style={{ background:"#e63946", border:"none", color:"white", padding:"6px 14px", borderRadius:"10px", cursor:"pointer", fontSize:"12px", fontWeight:"600" }}>Sí, eliminar</button>
              <button onClick={()=>setConfirmEliminar(false)} style={{ background:"rgba(255,255,255,0.1)", border:"none", color:"white", padding:"6px 14px", borderRadius:"10px", cursor:"pointer", fontSize:"12px" }}>Cancelar</button>
            </>
          ) : (
            <>
              <button onClick={()=>iniciarEdicion(detalle)} style={{ background:"rgba(255,255,255,0.1)", border:"1px solid rgba(255,255,255,0.3)", color:"white", padding:"6px 14px", borderRadius:"10px", cursor:"pointer", fontSize:"12px", fontWeight:"600" }}>✏️ Editar datos</button>
              <button onClick={()=>setConfirmEliminar(true)} style={{ background:"rgba(230,57,70,.2)", border:"1px solid rgba(230,57,70,.4)", color:"#e63946", padding:"6px 14px", borderRadius:"10px", cursor:"pointer", fontSize:"12px" }}>🗑 Eliminar</button>
            </>
          )}
        </div>
      </div>
      <div style={{ padding:"0 2rem 2rem", maxWidth:"700px", margin:"0 auto" }}>
        {ncGenerada && (
          <div style={{ background:"#fff0f0", border:"2px solid #e63946", borderRadius:"12px", padding:"1rem", marginBottom:"12px", textAlign:"center" }}>
            <p style={{ fontSize:"13px", fontWeight:"700", color:"#e63946", margin:0 }}>⚠️ NC generada automáticamente: {ncGenerada}</p>
          </div>
        )}
        {/* IDENTIFICACIÓN */}
        <div style={{ ...card, background:oscuro?"#0a1f5c":"#F0EEFF", border:`1px solid ${oscuro?"#7B5BF2":"#d4c8ff"}` }}>
          <h3 style={{ fontSize:"13px", fontWeight:"700", color:"#7B5BF2", marginBottom:"10px" }}>Identificación</h3>
          {[
            ["Código",detalle.codigoIngreso],["Fecha",detalle.fecha],["Tipo de material",detalle.tipoMaterial],
            ["Proveedor",detalle.proveedor],["N° Remito",detalle.remito],["Cantidad",`${detalle.cantidad} ${detalle.unidad}`],
            [TIPOS_CALIBRE.includes(detalle.tipoMaterial)?"Calibre":"Gramaje",detalle.gramaje?etiquetaGramaje(detalle.tipoMaterial, detalle.gramaje):null],["Medida de pliego",detalle.medidaPliego],
            ["Lote/Partida",detalle.lote],["FSC",detalle.esFSC?"✅ Material FSC certificado":"—"],
            ["Tiene remito",detalle.tieneRemito?"✅ Sí":"❌ No"],
            ["Registrado por",`${detalle.creadoPor} · ${detalle.rolCreador}`],
          ].map(([k,v])=>v&&(
            <div key={k} style={{ display:"flex", justifyContent:"space-between", fontSize:"13px", padding:"4px 0", borderBottom:`0.5px solid ${borde}` }}>
              <span style={{ color:sub }}>{k}</span>
              <span style={{ color:texto, fontWeight:"500" }}>{v}</span>
            </div>
          ))}
          {detalle.ncGenerada && (
            <div style={{ marginTop:"8px", padding:"6px 10px", background:"#fff0f0", borderRadius:"6px", border:"1px solid #e63946" }}>
              <p style={{ fontSize:"11px", color:"#e63946", fontWeight:"700", margin:0 }}>NC generada: {detalle.ncGenerada}</p>
            </div>
          )}
          <div style={{ marginTop:"10px", paddingTop:"10px", borderTop:`0.5px solid ${borde}` }}>
            <p style={{ fontSize:"11px", color:sub, fontWeight:"700", margin:"0 0 6px", textTransform:"uppercase", letterSpacing:".05em" }}>Certificado(s) de calidad</p>
            {(() => {
              const lista = certificadosDe(detalle);
              if (lista.length === 0) {
                return detalle.tieneCertificado
                  ? <p style={{ fontSize:"12px", color:"#f59e0b", margin:0 }}>⚠️ Certificado marcado sin archivo adjunto (registro anterior a la carga de archivos)</p>
                  : <p style={{ fontSize:"12px", color:sub, margin:0 }}>❌ No</p>;
              }
              return (
                <div style={{ display:"flex", flexWrap:"wrap", gap:"10px" }}>
                  {lista.map((c, i) => {
                    const url = certificadosUrls[c.path];
                    const esImagen = c.tipo?.startsWith("image/");
                    if (!url) return <p key={c.path||i} style={{ fontSize:"12px", color:sub, margin:0 }}>Cargando {i+1}/{lista.length}...</p>;
                    return esImagen ? (
                      <a key={c.path||i} href={url} target="_blank" rel="noreferrer">
                        <img src={url} alt={c.nombre || `Certificado ${i+1}`} style={{ maxWidth:"120px", maxHeight:"120px", borderRadius:"8px", border:`1px solid ${borde}`, cursor:"pointer", display:"block" }}/>
                      </a>
                    ) : (
                      <a key={c.path||i} href={url} target="_blank" rel="noreferrer" style={{ fontSize:"13px", color:"#7B5BF2", fontWeight:"600", textDecoration:"none" }}>
                        📄 {c.nombre || `Ver certificado ${i+1} (PDF)`}
                      </a>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        </div>
        {/* INSPECCIÓN */}
        <div style={card}>
          <h3 style={{ fontSize:"13px", fontWeight:"700", color:"#e63946", marginBottom:"10px" }}>Inspección de calidad</h3>
          {[
            ["Resultado inspección visual",detalle.inspeccionVisual],
            [TIPOS_CALIBRE.includes(detalle.tipoMaterial)?"Calibre verificado":"Gramaje verificado",detalle.gramaje?etiquetaGramaje(detalle.tipoMaterial, detalle.gramaje):"No verificado"],
            ["Observaciones",detalle.observaciones],
            ["Motivo rechazo/cuarentena",detalle.motivoRechazo],
          ].map(([k,v])=>v&&(
            <div key={k} style={{ padding:"6px 0", borderBottom:`0.5px solid ${borde}` }}>
              <p style={{ fontSize:"11px", color:sub, margin:"0 0 2px", fontWeight:"600" }}>{k}</p>
              <p style={{ fontSize:"13px", color:texto, margin:0 }}>{v}</p>
            </div>
          ))}
        </div>
        {/* PALLETS YA GENERADOS CON ESTE INGRESO */}
        {esCalidad && detalle.estado === "aceptado" && (
          <div style={{ ...card, border:"1px solid #7B5BF240", background: oscuro?"#0a1f5c":"#f5f3ff" }}>
            <h3 style={{ fontSize:"13px", fontWeight:"700", color:"#7B5BF2", marginBottom:"10px" }}>📦 Pallets de este ingreso</h3>
            <p style={{ fontSize:"12px", color:sub, margin:"0 0 10px" }}>
              Ingreso: <strong>{detalle.cantidad} {detalle.unidad}</strong> de <strong>{detalle.tipoMaterial}</strong>
            </p>
            {detalle.palletGenerado ? (
              <p style={{ fontSize:"12px", color:"#22c55e", marginBottom:"10px" }}>✅ Pallets creados: {Array.isArray(detalle.palletGenerado) ? detalle.palletGenerado.join(", ") : detalle.palletGenerado}</p>
            ) : (
              <p style={{ fontSize:"12px", color:sub, marginBottom:"10px" }}>Todavía no se generó ningún pallet para este ingreso.</p>
            )}
            <div style={{ display:"flex", gap:"8px", flexWrap:"wrap" }}>
              {detalle.palletGenerado && (
                <button onClick={() => {
                  const stockActual = storage.get("alqos_stock_pallets");
                  const pallets = stockActual.filter(p => p.codigoIngreso === detalle.codigoIngreso);
                  if (!pallets.length) { alert("No encontré los pallets de este ingreso en el stock local."); return; }
                  imprimirPalletsLote(pallets, usuario?.empresa);
                }}
                  style={{ padding:"8px 16px", background:"#09184D", color:"white", border:"none", borderRadius:"8px", cursor:"pointer", fontWeight:"700", fontSize:"13px" }}>
                  🖨️ Imprimir rótulos
                </button>
              )}
              <button onClick={() => { setIngresoParaPallets(detalle); setVista("pallets"); }}
                style={{ padding:"8px 16px", background:"#7B5BF2", color:"white", border:"none", borderRadius:"8px", cursor:"pointer", fontWeight:"700", fontSize:"13px" }}>
                📦 Agregar más pallets
              </button>
            </div>
          </div>
        )}
        {esCalidad && (
          <div style={card}>
            <h3 style={{ fontSize:"13px", fontWeight:"700", color:"#22c55e", marginBottom:"10px" }}>Estado del ingreso</h3>
            <div style={{ display:"flex", gap:"8px", flexWrap:"wrap" }}>
              {Object.entries(estadoLabel).map(([val,lab])=>(
                <button key={val} onClick={()=>cambiarEstado(detalle.id,val)}
                  style={{ padding:"7px 12px", borderRadius:"10px", fontSize:"12px", cursor:"pointer", fontWeight:"600", border:detalle.estado===val?`2px solid ${estadoColor[val]}`:`1px solid ${borde}`, background:detalle.estado===val?`${estadoColor[val]}22`:cardBg, color:detalle.estado===val?estadoColor[val]:sub }}>
                  {lab}
                </button>
              ))}
            </div>
            {detalle.estado==="cuarentena"&&(
              <div style={{ marginTop:"10px", padding:"10px", background:oscuro?"#1a1000":"#fffbeb", borderRadius:"8px", border:"1px solid #f59e0b40" }}>
                <p style={{ fontSize:"12px", color:"#f59e0b", fontWeight:"600", margin:"0 0 4px" }}>⏳ Material en cuarentena</p>
                <p style={{ fontSize:"11px", color:sub, margin:0 }}>Decidí si aceptás condicionado o devolvés al proveedor. Si devolvés se genera NC automáticamente.</p>
              </div>
            )}
          </div>
        )}
        {/* HISTORIAL */}
        {detalle.historialEstados?.length>0&&(
          <div style={card}>
            <h3 style={{ fontSize:"13px", fontWeight:"700", color:"#7B5BF2", marginBottom:"8px" }}>📋 Historial de estados</h3>
            {detalle.historialEstados.map((h,i)=>(
              <div key={i} style={{ display:"flex", justifyContent:"space-between", padding:"5px 0", borderBottom:`0.5px solid ${borde}`, fontSize:"12px" }}>
                <span style={{ color:sub }}>{h.fecha?.slice(0,16).replace("T"," ")}</span>
                <span style={{ color:estadoColor[h.estado], fontWeight:"600" }}>{estadoLabel[h.estado]}</span>
                <span style={{ color:sub }}>{h.por}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
  // ── NUEVO (material + proveedor + pallets/rótulos, todo en una pantalla) ──
  if (vista==="nuevo") {
    const totalDraft = palletsDraft.reduce((acc,p)=>acc+(parseFloat(p.cantidad)||0),0);
    const esCalibre = TIPOS_CALIBRE.includes(form.tipoMaterial);
    // Materiales frecuentes: combinaciones tipo+gramaje+medida ya usadas en
    // ingresos anteriores, para pickear con un clic en vez de tipear todo de
    // nuevo cada vez — pensado para cargar rápido leyendo directo del remito.
    const materialesFrecuentes = (() => {
      const mapa = new Map();
      items.forEach(i => {
        // Se excluyen los ingresos "devuelto" (rechazados al proveedor): sin
        // este filtro, un material cuya última carga fue un rechazo aparecía
        // igual como chip de "frecuente" y se podía autocompletar con un
        // clic sin que el operador supiera que la última vez ese material
        // vino mal.
        if (!i.tipoMaterial || i.estado === "devuelto") return;
        const key = `${i.tipoMaterial}|${i.gramaje||""}|${i.medidaPliego||""}`;
        if (!mapa.has(key)) mapa.set(key, { tipoMaterial:i.tipoMaterial, gramaje:i.gramaje||"", medidaPliego:i.medidaPliego||"", count:0 });
        mapa.get(key).count++;
      });
      return Array.from(mapa.values()).sort((a,b)=>b.count-a.count).slice(0,8);
    })();
    return (
    <div style={{ minHeight:"100vh", background:bg, fontFamily:"sans-serif" }}>
      <div style={{ background:navBg, padding:"1rem 1.5rem", display:"flex", alignItems:"center", justifyContent:"space-between", position:"sticky", top:0, zIndex:100, marginBottom:"1.5rem" }}>
        <div style={{ display:"flex", alignItems:"center", gap:"12px" }}>
          <button onClick={()=>{
            if (editandoId) { setEditandoId(null); setDetalle(items.find(i=>i.id===editandoId)); setVista("detalle"); }
            else setVista("listado");
            setPalletsDraft([]);
            setArchivosNuevos([]);
            setCertificadosExistentes([]);
          }} style={{ background:"rgba(255,255,255,0.1)", border:"none", color:"white", padding:"6px 14px", borderRadius:"10px", cursor:"pointer", fontSize:"13px" }}>← Volver</button>
          <span style={{ color:"white", fontWeight:"700", fontSize:"16px" }}>{editandoId ? "Editar ingreso" : "Nuevo ingreso de material"}</span>
        </div>
      </div>
      <div style={{ padding:"0 2rem 2rem", maxWidth:"700px", margin:"0 auto" }}>
        <div style={{ ...card, background:oscuro?"#0a1f5c":"#F0EEFF", border:`1px solid ${oscuro?"#7B5BF2":"#d4c8ff"}` }}>
          <p style={{ fontSize:"12px", color:sub, margin:0 }}>Registrado por: <strong style={{ color:texto }}>{usuario?.nombre}</strong> — {usuario?.rol}</p>
        </div>
        {/* MATERIAL */}
        <div style={card}>
          <h3 style={{ fontSize:"14px", fontWeight:"700", color:"#09184D", marginBottom:"8px" }}>Material recibido</h3>
          <span style={lbl}>Tipo de material *</span>
          <select value={form.tipoMaterial} onChange={e=>setForm(f=>({...f,tipoMaterial:e.target.value}))} style={inp}>
            {TIPOS_MATERIAL.map(t=><option key={t} value={t}>{t}</option>)}
          </select>
          {materialesFrecuentes.length > 0 && (
            <>
              <span style={lbl}>Materiales frecuentes — tocá para autocompletar</span>
              <div style={{ display:"flex", gap:"6px", flexWrap:"wrap", marginTop:"6px" }}>
                {materialesFrecuentes.map((m,idx) => (
                  <button key={idx} type="button"
                    onClick={() => setForm(f=>({...f, tipoMaterial:m.tipoMaterial, gramaje:m.gramaje, medidaPliego:m.medidaPliego}))}
                    style={{ padding:"6px 12px", borderRadius:"16px", fontSize:"11px", cursor:"pointer", fontWeight:"600", border:`1px solid ${borde}`, background: (form.tipoMaterial===m.tipoMaterial && (form.gramaje||"")===m.gramaje && (form.medidaPliego||"")===m.medidaPliego) ? "#7B5BF222" : cardBg, color: (form.tipoMaterial===m.tipoMaterial && (form.gramaje||"")===m.gramaje && (form.medidaPliego||"")===m.medidaPliego) ? "#7B5BF2" : sub }}>
                    {m.tipoMaterial}{m.gramaje?` · ${m.gramaje}${TIPOS_CALIBRE.includes(m.tipoMaterial)?" cal.":"g"}`:""}{m.medidaPliego?` · ${m.medidaPliego}`:""}
                  </button>
                ))}
              </div>
            </>
          )}
          {(form.tipoMaterial==="Papel"||form.tipoMaterial==="Cartón macizo"||form.tipoMaterial==="Cartulina")&&(
            <>
              <span style={lbl}>{esCalibre ? "Calibre" : "Gramaje (g/m²)"}</span>
              <input type="number" step={esCalibre ? "0.1" : "1"} value={form.gramaje||""} onChange={e=>setForm(f=>({...f,gramaje:e.target.value}))} style={inp} placeholder={esCalibre ? "Ej: 1.5, 2, 3" : "Ej: 90, 300, 450"}/>
              <span style={lbl}>Medida del pliego</span>
              <input value={form.medidaPliego||""} onChange={e=>setForm(f=>({...f,medidaPliego:e.target.value}))} style={inp} placeholder="Ej: 70x100, 65x92"/>
            </>
          )}
          <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr", gap:"10px" }}>
            <div>
              {(()=>{
                // En modo edición, si el ingreso YA tiene pallets reales
                // generados, bloqueamos "Cantidad total" — antes se podía
                // editar libre sin ninguna relación con la suma real de los
                // pallets que ya existen en stock, desincronizando el total
                // del ingreso respecto a lo que físicamente hay cargado, sin
                // ningún aviso. Para corregir la cantidad real hay que
                // corregir los pallets (Stock de Materiales), no este campo.
                const tienePalletsReales = !!editandoId && !!detalle?.palletGenerado &&
                  (Array.isArray(detalle.palletGenerado) ? detalle.palletGenerado.length > 0 : true);
                const bloqueado = palletsDraft.length > 0 || tienePalletsReales;
                return (<>
                  <span style={lbl}>
                    {tienePalletsReales ? "Cantidad total (bloqueada — ya tiene pallets reales)"
                      : palletsDraft.length > 0 ? "Cantidad total (autocalculada de los pallets)" : "Cantidad total *"}
                  </span>
                  <input
                    type="number"
                    value={form.cantidad||""}
                    onChange={e=>setForm(f=>({...f,cantidad:e.target.value}))}
                    readOnly={bloqueado}
                    style={{ ...inp, background: bloqueado ? (oscuro?"#0a1440":"#f0f0f5") : cardBg, cursor: bloqueado ? "not-allowed" : "text" }}
                    placeholder={bloqueado ? "" : "0 — o cargá los pallets abajo"}
                  />
                  {tienePalletsReales && (
                    <p style={{ fontSize:"11px", color:sub, margin:"4px 0 0" }}>Para corregir la cantidad real, corregí los pallets en Stock de Materiales.</p>
                  )}
                </>);
              })()}
            </div>
            <div>
              <span style={lbl}>Unidad</span>
              <select value={form.unidad} onChange={e=>setForm(f=>({...f,unidad:e.target.value}))} style={inp}>
                {UNIDADES.map(u=><option key={u} value={u}>{u}</option>)}
              </select>
            </div>
          </div>
          <span style={lbl}>Lote / Partida</span>
          <input value={form.lote||""} onChange={e=>setForm(f=>({...f,lote:e.target.value}))} style={inp} placeholder="N° de lote o partida del proveedor"/>
        </div>
        {/* PROVEEDOR */}
        <div style={card}>
          <h3 style={{ fontSize:"14px", fontWeight:"700", color:"#09184D", marginBottom:"8px" }}>Proveedor y documentación</h3>
          <span style={lbl}>Proveedor *</span>
          <input value={form.proveedor||""} onChange={e=>setForm(f=>({...f,proveedor:e.target.value}))} style={inp} placeholder="Nombre del proveedor"/>
          <div style={{ display:"flex", gap:"6px", flexWrap:"wrap", marginTop:"8px" }}>
            {PROVEEDORES_RAPIDOS.map(p=>(
              <button key={p} onClick={()=>setForm(f=>({...f,proveedor:p}))}
                style={{ padding:"4px 10px", borderRadius:"16px", fontSize:"11px", cursor:"pointer", border:`1px solid ${borde}`, background:form.proveedor===p?`#7B5BF222`:cardBg, color:form.proveedor===p?"#7B5BF2":sub }}>
                {p}
              </button>
            ))}
          </div>
          <span style={lbl}>N° de Remito</span>
          <input
            value={form.remito||""}
            onChange={e=>{
              const v = e.target.value;
              setForm(f=>({...f, remito:v, tieneRemito: v.trim().length>0}));
            }}
            style={inp}
            placeholder="N° remito del proveedor"
          />
          <span style={lbl}>Fecha de recepción</span>
          <input type="date" value={form.fecha} onChange={e=>setForm(f=>({...f,fecha:e.target.value}))} style={inp}/>
          {/* Checkboxes */}
          <div style={{ display:"flex", gap:"16px", marginTop:"14px", flexWrap:"wrap" }}>
            <label style={{ display:"flex", alignItems:"center", gap:"6px", cursor:"pointer", fontSize:"12px", color:texto }}>
              <input type="checkbox" checked={form.esFSC||false} onChange={e=>setForm(f=>({...f,esFSC:e.target.checked}))} style={{ width:"16px", height:"16px" }}/>
              Material FSC certificado
            </label>
          </div>
          {/* Certificado de calidad: archivo real (PDF/imagen), no un tilde --
              ISO 9001 pide la evidencia documentada del proveedor, no solo el
              registro de que "existe". */}
          <span style={lbl}>Certificado(s) de calidad del proveedor</span>
          {(certificadosExistentes.length > 0 || archivosNuevos.length > 0) && (
            <div style={{ marginTop:"6px" }}>
              {certificadosExistentes.map((c, i) => (
                <div key={`ex-${c.path}`} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"5px 10px", borderRadius:"8px", background:oscuro?"#0a1f5c":"#f5f3ff", border:`1px solid ${borde}`, marginBottom:"4px" }}>
                  <span style={{ fontSize:"12px", color:texto }}>📎 {c.nombre || `Certificado ${i+1}`}</span>
                  <button type="button" onClick={() => setCertificadosExistentes(l => l.filter((_,idx) => idx!==i))}
                    style={{ background:"none", border:"none", color:"#e63946", cursor:"pointer", fontSize:"15px", lineHeight:1 }}>✕</button>
                </div>
              ))}
              {archivosNuevos.map((f, i) => (
                <div key={`nu-${i}-${f.name}`} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"5px 10px", borderRadius:"8px", background:oscuro?"#0a2e1a":"#f0fdf4", border:"1px solid #22c55e40", marginBottom:"4px" }}>
                  <span style={{ fontSize:"12px", color:"#22c55e" }}>✅ {f.name}</span>
                  <button type="button" onClick={() => setArchivosNuevos(l => l.filter((_,idx) => idx!==i))}
                    style={{ background:"none", border:"none", color:"#e63946", cursor:"pointer", fontSize:"15px", lineHeight:1 }}>✕</button>
                </div>
              ))}
            </div>
          )}
          <input type="file" ref={inputFotoCertificadoRef} accept="image/*" capture="environment" style={{ display:"none" }}
            onChange={e => { const f = e.target.files?.[0] || null; if (f) setArchivosNuevos(l => [...l, f]); e.target.value = ""; }}/>
          <input type="file" ref={inputArchivoCertificadoRef} accept={TIPOS_CERTIFICADO_PERMITIDOS.join(",")} style={{ display:"none" }}
            onChange={e => {
              const f = e.target.files?.[0] || null;
              if (f && !TIPOS_CERTIFICADO_PERMITIDOS.includes(f.type)) {
                alert("Formatos aceptados: PDF, JPG o PNG.");
                e.target.value = "";
                return;
              }
              if (f) setArchivosNuevos(l => [...l, f]);
              e.target.value = "";
            }}/>
          <div style={{ display:"flex", gap:"8px", marginTop:"4px", flexWrap:"wrap" }}>
            <button type="button" onClick={() => inputFotoCertificadoRef.current?.click()}
              style={{ padding:"7px 14px", background:"#7B5BF2", color:"white", border:"none", borderRadius:"8px", cursor:"pointer", fontWeight:"600", fontSize:"12px" }}>
              📷 Sacar foto
            </button>
            <button type="button" onClick={() => inputArchivoCertificadoRef.current?.click()}
              style={{ padding:"7px 14px", background:"none", border:`1.5px solid ${borde}`, color:texto, borderRadius:"8px", cursor:"pointer", fontWeight:"600", fontSize:"12px" }}>
              📎 Subir archivo
            </button>
          </div>
          <p style={{ fontSize:"11px", color:sub, margin:"4px 0 0" }}>Podés adjuntar varios (ej. certificado en dos hojas, o certificado + foto del remito). "Sacar foto" abre la cámara directo en el celular. Es la evidencia real que pide ISO 9001 para control de información documentada de proveedores.</p>
        </div>
        {/* INSPECCIÓN */}
        <div style={card}>
          <h3 style={{ fontSize:"14px", fontWeight:"700", color:"#e63946", marginBottom:"8px" }}>Inspección de calidad</h3>
          <span style={lbl}>Resultado inspección visual</span>
          <select value={form.inspeccionVisual||""} onChange={e=>setForm(f=>({...f,inspeccionVisual:e.target.value}))} style={inp}>
            <option value="">Seleccioná...</option>
            <option>Conforme — sin observaciones</option>
            <option>Conforme con observaciones menores</option>
            <option>No conforme — daño visible</option>
            <option>No conforme — gramaje incorrecto</option>
            <option>No conforme — medidas incorrectas</option>
            <option>No conforme — humedad / contaminación</option>
          </select>
          <span style={lbl}>Observaciones</span>
          <textarea rows={2} value={form.observaciones||""} onChange={e=>setForm(f=>({...f,observaciones:e.target.value}))} style={{...inp,resize:"none"}} placeholder="Describí el estado del material recibido..."/>
          <span style={lbl}>Resultado final *</span>
          {editandoId && (
            <p style={{ fontSize:"11px", color:sub, margin:"4px 0 0" }}>El estado (aceptado/cuarentena/etc.) se cambia desde "Estado del ingreso" en el detalle, no acá.</p>
          )}
          <div style={{ display:"flex", gap:"8px", flexWrap:"wrap", marginTop:"8px", opacity: editandoId ? 0.55 : 1 }}>
            {Object.entries(estadoLabel).map(([val,lab])=>(
              <button key={val} disabled={!!editandoId} onClick={()=>setForm(f=>({...f,estado:val}))}
                style={{ padding:"8px 14px", borderRadius:"10px", fontSize:"12px", cursor: editandoId ? "not-allowed" : "pointer", fontWeight:"600", border:form.estado===val?`2px solid ${estadoColor[val]}`:`1px solid ${borde}`, background:form.estado===val?`${estadoColor[val]}22`:cardBg, color:form.estado===val?estadoColor[val]:sub }}>
                {lab}
              </button>
            ))}
          </div>
          {(form.estado==="cuarentena"||form.estado==="devuelto")&&(
            <>
              <span style={lbl}>Motivo de rechazo / cuarentena *</span>
              <textarea rows={2} value={form.motivoRechazo||""} onChange={e=>setForm(f=>({...f,motivoRechazo:e.target.value}))} style={{...inp,resize:"none"}} placeholder="Describí por qué el material no fue aceptado..."/>
              {form.estado==="devuelto"&&(
                <div style={{ marginTop:"8px", padding:"8px 12px", background:"#fff0f0", borderRadius:"8px", border:"1px solid #e6394640" }}>
                  <p style={{ fontSize:"11px", color:"#e63946", fontWeight:"600", margin:0 }}>⚠️ Se generará una NC automáticamente al guardar</p>
                </div>
              )}
            </>
          )}
        </div>
        {/* PALLETS — carga inline, en la misma pantalla, solo si el resultado es aceptado/aceptado condicionado.
            No se muestra en modo edición: los pallets de un ingreso ya guardado se manejan desde
            "Agregar más pallets" en el detalle, no reescribiendo el ingreso. */}
        {!editandoId && ["aceptado","aceptado_condicionado"].includes(form.estado) && (
          <div style={{ ...card, border:"1px solid #7B5BF240", background: oscuro?"#0a1f5c":"#f5f3ff" }}>
            <h3 style={{ fontSize:"14px", fontWeight:"700", color:"#7B5BF2", marginBottom:"4px" }}>📦 Pallets / unidades de este remito</h3>
            <p style={{ fontSize:"11px", color:sub, margin:"0 0 10px" }}>
              Dividí este ingreso en los pallets físicos reales (mismo gramaje/medida cargados arriba). Si no cargás ninguno acá, se genera 1 pallet automático con el total.
            </p>
            <div style={{ display:"flex", gap:"6px", flexWrap:"wrap", marginTop:"6px" }}>
              {CONTENEDORES.map(c => (
                <button key={c.id} type="button" onClick={() => setFormPallet(f => ({ ...f, contenedor: c.id }))}
                  style={{ padding:"6px 10px", borderRadius:"8px", fontSize:"11px", cursor:"pointer", fontWeight:"600", border: formPallet.contenedor === c.id ? "2px solid #7B5BF2" : "1px solid "+borde, background: formPallet.contenedor === c.id ? "#7B5BF215" : cardBg, color: formPallet.contenedor === c.id ? "#7B5BF2" : sub }}>
                  {c.icono} {c.label}
                </button>
              ))}
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"2fr 2fr 1fr", gap:"10px" }}>
              <div>
                <span style={lbl}>Cantidad de este pallet *</span>
                <input type="number" value={formPallet.cantidad} onChange={e=>setFormPallet(f=>({...f,cantidad:e.target.value}))} style={inp} placeholder={`en ${form.unidad}`}/>
              </div>
              <div>
                <span style={lbl}>Lote (si difiere del general)</span>
                <input value={formPallet.lote} onChange={e=>setFormPallet(f=>({...f,lote:e.target.value}))} style={inp} placeholder={form.lote || "N° lote"}/>
              </div>
              <div style={{ display:"flex", alignItems:"flex-end" }}>
                <button onClick={agregarPalletDraft} style={{ width:"100%", padding:"10px", background:"#7B5BF2", color:"white", border:"none", borderRadius:"8px", cursor:"pointer", fontWeight:"700", fontSize:"12px", marginTop:"4px" }}>
                  + Agregar
                </button>
              </div>
            </div>
            {palletsDraft.length > 0 && (
              <div style={{ marginTop:"14px" }}>
                <p style={{ fontSize:"11px", color:sub, fontWeight:"700", textTransform:"uppercase", margin:"0 0 6px" }}>
                  Pallets cargados ({palletsDraft.length}) — total: {totalDraft} {form.unidad}
                </p>
                {palletsDraft.map((p) => {
                  const cInfo = CONTENEDORES.find(c=>c.id===p.contenedor);
                  return (
                    <div key={p._key} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"8px 10px", borderRadius:"8px", background:cardBg, border:`1px solid ${borde}`, marginBottom:"6px" }}>
                      <span style={{ fontSize:"12px", color:texto }}>{cInfo?.icono} {cInfo?.label} · {p.cantidad} {form.unidad}{p.lote?` · lote ${p.lote}`:""}</span>
                      <button onClick={()=>quitarPalletDraft(p._key)} style={{ background:"none", border:"none", color:"#e63946", cursor:"pointer", fontSize:"16px", lineHeight:1 }}>×</button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
        <button disabled={guardando} onClick={editandoId ? guardarEdicion : guardar} style={{ width:"100%", padding:"14px", background:"linear-gradient(135deg,#7B5BF2,#09184D)", color:"white", border:"none", borderRadius:"10px", fontSize:"15px", fontWeight:"700", cursor:guardando?"default":"pointer", marginTop:"1rem", opacity:guardando?0.6:1 }}>
          {guardando ? "Guardando..." : (editandoId ? "Guardar cambios" : (palletsDraft.length > 0 ? `Registrar ingreso y generar ${palletsDraft.length} pallet${palletsDraft.length>1?"s":""} + rótulos` : "Registrar ingreso"))}
        </button>
      </div>
    </div>
    );
  }
  // Nota (auditoría 13/08): había un "MODAL RÓTULO" acá (RotuloModal, activado
  // por un estado rotuloIngreso) que quedó inalcanzable — nada en la interfaz
  // llama setRotuloIngreso con un valor real, solo con null para cerrarlo. Se
  // borró: usaba el viejo patrón window.open()+document.write()+print() que
  // tenía el mismo problema de ventana en blanco que ya se resolvió en
  // imprimirPalletsLote() (que además genera un PDF real, mejor que esto).
  // El botón "🖨️ Rótulo" individual de la pantalla de pallets ya usa
  // imprimirPalletsLote([pallet]).
  // ── LISTADO ──
  return (
    <div style={{ minHeight:"100vh", background:bg, fontFamily:"sans-serif" }}>
      <div style={{ background:navBg, padding:"1rem 1.5rem", display:"flex", alignItems:"center", justifyContent:"space-between", position:"sticky", top:0, zIndex:100 }}>
        <div style={{ display:"flex", alignItems:"center", gap:"12px" }}>
          <button onClick={onVolver} style={{ background:"rgba(255,255,255,0.1)", border:"none", color:"white", padding:"6px 14px", borderRadius:"10px", cursor:"pointer", fontSize:"13px" }}>← Menú</button>
          <BotonInstructivo moduloKey="ingreso_materiales" oscuro={oscuro} />
          {usuario?.rol === "Administrador" && <BotonMapaProceso moduloKey="ingreso_materiales" oscuro={oscuro} />}
          <span style={{ color:"white", fontWeight:"700", fontSize:"16px" }}>📦 Ingreso de Materiales</span>
        </div>
        <CampanaNotificaciones oscuro={oscuro} modulo="IngresoMateriales" />
          {esCalidad&&<button onClick={()=>setVista("nuevo")} style={{ padding:"6px 14px", background:"#7B5BF2", color:"white", border:"none", borderRadius:"8px", cursor:"pointer", fontSize:"13px", fontWeight:"600" }}>+ Nuevo ingreso</button>}
      </div>
      <div style={{ padding:"1rem 1.5rem" }}>
        {ncGenerada&&(
          <div style={{ background:"#fff0f0", border:"2px solid #e63946", borderRadius:"12px", padding:"12px 16px", marginBottom:"1rem" }}>
            <p style={{ fontSize:"13px", fontWeight:"700", color:"#e63946", margin:0 }}>⚠️ NC generada automáticamente: {ncGenerada} — revisá el módulo de No Conformidades</p>
          </div>
        )}
        {/* KPIs */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(130px,1fr))", gap:"10px", marginBottom:"1.5rem" }}>
          {[
            ["Total",resumen.total,"#7B5BF2"],
            ["Aceptados",resumen.aceptados,"#22c55e"],
            ["Cuarentena",resumen.cuarentena,"#f59e0b"],
            ["Devueltos",resumen.devueltos,"#e63946"],
            ["FSC",resumen.esFSC,"#22c55e"],
          ].map(([lab,val,col])=>(
            <div key={lab} style={{ background:cardBg, borderRadius:"12px", padding:"1rem", border:`1px solid ${borde}`, textAlign:"center" }}>
              <p style={{ fontSize:"22px", fontWeight:"700", color:col, margin:0 }}>{val}</p>
              <p style={{ fontSize:"11px", color:sub, margin:"4px 0 0" }}>{lab}</p>
            </div>
          ))}
        </div>
        {/* FILTROS */}
        <input placeholder="🔍 Buscar por código, proveedor o material..." value={busqueda} onChange={e=>setBusqueda(e.target.value)} style={{...inp, marginBottom:"1rem", marginTop:0}}/>
        <div style={{ display:"flex", gap:"6px", flexWrap:"wrap", marginBottom:"1rem" }}>
          <button onClick={()=>setFiltroEstado("todos")} style={{ padding:"4px 10px", borderRadius:"16px", fontSize:"11px", cursor:"pointer", fontWeight:"600", border:filtroEstado==="todos"?"2px solid #7B5BF2":`1px solid ${borde}`, background:filtroEstado==="todos"?"rgba(123,91,242,.1)":cardBg, color:filtroEstado==="todos"?"#7B5BF2":sub }}>Todos</button>
          {Object.entries(estadoLabel).map(([val,lab])=>(
            <button key={val} onClick={()=>setFiltroEstado(val)} style={{ padding:"4px 10px", borderRadius:"16px", fontSize:"11px", cursor:"pointer", fontWeight:"600", border:filtroEstado===val?`2px solid ${estadoColor[val]}`:`1px solid ${borde}`, background:filtroEstado===val?`${estadoColor[val]}22`:cardBg, color:filtroEstado===val?estadoColor[val]:sub }}>{lab}</button>
          ))}
        </div>
        {/* LISTA */}
        {itemsFiltrados.length===0
          ? <div style={{ textAlign:"center", padding:"4rem" }}>
              <p style={{ fontSize:"40px" }}>📦</p>
              <p style={{ fontSize:"15px", color:texto }}>Sin ingresos registrados</p>
              <p style={{ fontSize:"13px", color:sub }}>Registrá el primer ingreso de material</p>
            </div>
          : itemsFiltrados.map(i=>(
              <div key={i.id} onClick={()=>{setDetalle(items.find(x=>x.id===i.id)||i);setVista("detalle");setNcGenerada(null);}}
                style={{ background:cardBg, borderRadius:"12px", padding:"1.2rem 1.5rem", border:`1px solid ${i.estado==="cuarentena"?"#f59e0b":i.estado==="devuelto"?"#e63946":borde}`, marginBottom:"10px", cursor:"pointer" }}
                onMouseEnter={e=>e.currentTarget.style.boxShadow="0 4px 16px rgba(123,91,242,0.15)"}
                onMouseLeave={e=>e.currentTarget.style.boxShadow="none"}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                  <div>
                    <span style={{ fontWeight:"700", color:"#7B5BF2", fontSize:"13px" }}>{i.codigoIngreso}</span>
                    <span style={{ marginLeft:"8px", fontSize:"13px", color:texto, fontWeight:"600" }}>{i.tipoMaterial}</span>
                    {i.esFSC&&<span style={{ marginLeft:"6px", fontSize:"10px", color:"#22c55e", background:"#22c55e15", padding:"2px 6px", borderRadius:"6px", fontWeight:"600" }}>FSC</span>}
                    {i.ncGenerada&&<span style={{ marginLeft:"6px", fontSize:"10px", color:"#e63946", background:"#e6394615", padding:"2px 6px", borderRadius:"6px", fontWeight:"600" }}>NC: {i.ncGenerada}</span>}
                    <p style={{ fontSize:"12px", color:sub, marginTop:"4px" }}>{i.proveedor} · {i.cantidad} {i.unidad}{i.gramaje?` · ${etiquetaGramaje(i.tipoMaterial, i.gramaje)}`:""}</p>
                  </div>
                  <span style={{ fontSize:"11px", fontWeight:"700", color:estadoColor[i.estado] }}>{estadoLabel[i.estado]}</span>
                </div>
                <div style={{ marginTop:"6px", fontSize:"11px", color:oscuro?"#7B5BF2":"#9b8cc4" }}>
                  {i.fecha} · {i.creadoPor}
                </div>
              </div>
            ))
        }
      </div>
    </div>
  );
}
